/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/*
 * Used to get access to router context.
 */
import { useNavigate, useParams } from 'react-router-dom';

/*
 * Used to style components
 */
import { Box, Checkbox, FormControlLabel, Paper, styled, Tooltip, TooltipProps } from '@mui/material';

/**
 * Used for the basic page layout.
 */
import {
    ALL_INSTITUTIONS_CODE,
    InstitutionBreadcrumbs,
    InstitutionsContext,
    PageLayout,
    RouteLoading,
    TrialContextSelector
} from '@ngt/opms';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */

/*
 * Used to type patient state.
 */
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { ImageUploadUpdaterProvider } from '../../components/providers/form/ImageUploadUpdaterProvider';
import MriImageUploadsProvider from '../../components/providers/form/MriImageUploadsProvider';
import ImageUploadTable from './ImageUploadTable';

/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IRegistrationProps {

}

// interface IPatientTableProps {
//     title: string;
//     entityName: string;
//     columns: Array<GridColDef<Dtos.Patient>>;
//     onRowClick?: GridEventListener<'rowClick'>;
//     patientStates?: Array<number>;
//     startCollapsed?: boolean;
//     patientsToUse?: Array<Dtos.Patient>;
// }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const PaperContainer = styled(Paper)(({theme}) => ({
    padding: theme.spacing(3)
}));

const CheckboxArea = styled(Box)(({theme}) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2)
}));

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ tooltip: className }} />
))(({ theme }) => ({
    
    backgroundColor: theme.palette.mode === 'dark' ?
        `rgba(0, 0, 0, 0.87)` :
        theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
    theme.palette.common.white :
        `rgba(0, 0, 0, 0.87)`,
    boxShadow: theme.shadows[1],
    fontSize: 11,
    
}));
/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */


const ImageUploadPage: FC<IRegistrationProps> = () => {
    const navigate = useNavigate();

    const { masterGroupCode, collaboratingGroupCode, countryCode, institutionCode } = useParams();

    const parsedInstitutionCode = useMemo(() => {
        return institutionCode !== ALL_INSTITUTIONS_CODE ? institutionCode : undefined;
    }, [institutionCode]);

    const { data: institutions } = useContext(InstitutionsContext);

    const institution = useMemo(() => {
        return institutions?.find(institution => institution.code === parsedInstitutionCode);
    }, [parsedInstitutionCode, institutions]);

    const institutionsToUse = useMemo(() => {
        return institutions?.filter(i => i.code);
    }, [institutions]);

    const {
        permissions: [
            canAdministerOpms,
            isSiteUSer,
            canCentralReview
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsAdminister,
            Permission.IsSiteUser,
            Permission.CanCentralReview
        ], 
        undefined, 
        undefined, 
        institution?.id
    );

    const isCentralReviewer = useMemo(() => {
        return !canAdministerOpms && canCentralReview
    }, [canAdministerOpms, canCentralReview]);

    //@ts-expect-error
    const onSelect = useCallback((newMasterGroupCode?: string | null, newCollaboratingGroupCode?: string | null, newCountryCode?: string | null, newInstitutionCode?: string | null) => {
        if (newInstitutionCode) {
            navigate(`/image-upload/${newInstitutionCode}`);
        }
        else {
            navigate(`/image-upload`);
        }

        return;
    }, [navigate]);

    const [selectedImagesNotUploaded, setSelectedImagesNotUploaded] = useState(false);
    const [selectedImagesNotVerified, setSelectedImagesNotVerified] = useState(false);
    const [selectedImagesDeidentifiedFailed, setSelectedImagesDeidentifiedFailed] = useState(false);

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<InstitutionBreadcrumbs />}
            heading="Baseline Image Upload"
        >
            <ImageUploadUpdaterProvider>
                <PaperContainer>
                    <TrialContextSelector
                        onChange={onSelect}
                        allowAllMasterGroups={true}
                        allowAllCollaboratingGroups={true}
                        allowAllCountries={true}
                        allowAllInstitutions={true}
                        hideMasterGroups={true}
                        hideCollaboratingGroups={true}
                        hideCountries={true}
                        masterGroupCode={masterGroupCode}
                        collaboratingGroupCode={collaboratingGroupCode}
                        countryCode={countryCode}
                        institutionCode={institutionCode}
                        institutions={institutionsToUse}
                    >
                    </TrialContextSelector>
                    
                    {
                        !isCentralReviewer && 
                        <CheckboxArea>
                            <StyledTooltip title="Only show participants with uploads.">
                                <FormControlLabel 
                                    control={
                                        <Checkbox
                                            checked={selectedImagesNotUploaded}
                                            onChange={() => {
                                                setSelectedImagesNotUploaded(x => !x);
                                            }}
                                            name="notUploaded"
                                            color="primary"
                                        />
                                    } 
                                    label="Not Uploaded" 
                                />
                            </StyledTooltip>
                            <StyledTooltip title="Only show participants with uploads not verified.">
                                <FormControlLabel 
                                    control={
                                        <Checkbox
                                            checked={selectedImagesNotVerified}
                                            onChange={() => {
                                                setSelectedImagesNotVerified(x => !x);
                                            }}
                                            name="notVerified"
                                            color="primary"
                                        />
                                    } 
                                    label="Not Verified" 
                                />
                            </StyledTooltip>
                            <StyledTooltip title="Only show participants with uploads that failed de-identification.">
                                <FormControlLabel 
                                    control={
                                        <Checkbox
                                            checked={selectedImagesDeidentifiedFailed}
                                            onChange={() => {
                                                setSelectedImagesDeidentifiedFailed(x => !x);
                                            }}
                                            name="deidentifiedFailed"
                                            color="primary"
                                        />
                                    } 
                                    label="De-identification Failed"
                                />
                            </StyledTooltip>
                        </CheckboxArea>
                    }

                    <MriImageUploadsProvider 
                        selectedImagesDeidentifiedFailed={selectedImagesDeidentifiedFailed}
                        selectedImagesNotUploaded={selectedImagesNotUploaded}
                        selectedImagesNotVerified={selectedImagesNotVerified}
                        canAdministerOpms={canAdministerOpms}
                        isCentralReviewer={isCentralReviewer}
                    >
                        <ImageUploadTable
                            canAdministerOpms={canAdministerOpms}
                            isSiteUSer={isSiteUSer}
                            isCentralReviewer={isCentralReviewer}
                        />
                    </MriImageUploadsProvider>
                </PaperContainer>
            </ImageUploadUpdaterProvider>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default ImageUploadPage;
